/**
 * Build JSON body for PUT api/v2/.../tests/:id/survey (full survey list).
 */

const CHOICE_OPTION_TYPES = ["singlechoice", "multiplechoice", "rankingscale"];
const SCALE_OPTION_TYPES = ["likertscale", "likertscale_single", "ratingscale"];
const TYPES_WITH_PUT_OPTIONS = [...CHOICE_OPTION_TYPES, ...SCALE_OPTION_TYPES];

function isQuestionLogic(q) {
	return q && (q.is_logic === 1 || q.is_logic === true);
}

function buildIdToIndex(questions) {
	const idToIndex = new Map();
	questions.forEach((q, idx) => {
		if (q && q._surveyQuestionPk != null && q._surveyQuestionPk !== "") {
			idToIndex.set(Number(q._surveyQuestionPk), idx);
		}
		if (q && q.id != null && q.id !== "") {
			idToIndex.set(Number(q.id), idx);
		}
	});
	return idToIndex;
}

/** True when jump resolves to this question or an earlier one in list order (invalid forward branching). */
export function isBackwardOrSameJump(sourceIndex, rawJumpValue, questions) {
	const tIdx = resolveTargetIndex(rawJumpValue, questions);
	if (tIdx === null || tIdx === -1) {
		return false;
	}
	return tIdx <= sourceIndex;
}

/**
 * After reordering the question array (same object references, new order), remap stored jump values
 * so they still point at the same survey rows. Uses stable matching via `resolveTargetIndex` on the
 * pre-reorder list and `getSurveyJumpTargetId` on the post-reorder row.
 */
export function remapJumpValueToNewOrder(rawValue, questionsBeforeReorder, questionsAfterReorder) {
	if (
		rawValue === "End Survey" ||
		rawValue === null ||
		rawValue === undefined ||
		rawValue === ""
	) {
		return rawValue;
	}
	if (typeof rawValue === "object" && rawValue !== null && "value" in rawValue) {
		return remapJumpValueToNewOrder(rawValue.value, questionsBeforeReorder, questionsAfterReorder);
	}
	const tIdx = resolveTargetIndex(rawValue, questionsBeforeReorder);
	if (tIdx === null || tIdx === -1) {
		return rawValue;
	}
	const targetQ = questionsBeforeReorder[tIdx];
	const newIdx = questionsAfterReorder.findIndex((q) => q === targetQ);
	if (newIdx < 0) {
		return "End Survey";
	}
	return getSurveyJumpTargetId(questionsAfterReorder[newIdx]);
}

function syncSingleChoiceOptionJumpFields(q) {
	if (!q || q.questionType !== "singlechoice" || !Array.isArray(q.option)) {
		return q;
	}
	const jumpTo = Array.isArray(q.jumpTo) ? q.jumpTo : [];
	const next = { ...q };
	next.option = q.option.map((opt, oi) => {
		if (!opt || typeof opt !== "object") {
			return opt;
		}
		const j = jumpTo[oi];
		if (j === undefined) {
			return opt;
		}
		return { ...opt, jump_to_question: j, jumpTo: j };
	});
	return next;
}

/** Remap all logic jump fields on `questionsAfterReorder` using list positions before vs after reorder. */
export function remapSurveyJumpTargetsAfterReorder(questionsBeforeReorder, questionsAfterReorder) {
	if (!Array.isArray(questionsAfterReorder)) {
		return questionsAfterReorder;
	}
	return questionsAfterReorder.map((q) => {
		if (!q || typeof q !== "object") {
			return q;
		}
		let next = { ...q };
		if (Array.isArray(q.jumpTo) && q.jumpTo.length > 0) {
			next.jumpTo = q.jumpTo.map((j) =>
				remapJumpValueToNewOrder(j, questionsBeforeReorder, questionsAfterReorder),
			);
		}
		if (q.jump_to_question != null && q.jump_to_question !== "") {
			next.jump_to_question = remapJumpValueToNewOrder(
				q.jump_to_question,
				questionsBeforeReorder,
				questionsAfterReorder,
			);
		}
		if (Array.isArray(next.option) && next.option.length > 0) {
			next.option = next.option.map((opt) => {
				if (!opt || typeof opt !== "object") {
					return opt;
				}
				const jj = opt.jump_to_question != null ? opt.jump_to_question : opt.jumpTo;
				if (jj == null || jj === "") {
					return opt;
				}
				const nv = remapJumpValueToNewOrder(jj, questionsBeforeReorder, questionsAfterReorder);
				return { ...opt, jump_to_question: nv, jumpTo: nv };
			});
		}
		if (next.questionType === "singlechoice" && Array.isArray(next.jumpTo)) {
			next = syncSingleChoiceOptionJumpFields(next);
		}
		return next;
	});
}

/** List of logic rows whose jump targets are same or earlier than the parent (0-based indices). */
export function findBackwardJumpViolations(questions) {
	const violations = [];
	if (!Array.isArray(questions)) {
		return violations;
	}
	for (let i = 0; i < questions.length; i++) {
		const q = questions[i];
		if (!isQuestionLogic(q)) {
			continue;
		}
		if (q.questionType === "singlechoice") {
			const oc = Math.max(
				extractOptionLabelsFromQuestion(q).length,
				Array.isArray(q.jumpTo) ? q.jumpTo.length : 0,
			);
			for (let oi = 0; oi < oc; oi++) {
				const j = getSingleChoiceJumpTarget(q, oi);
				if (isBackwardOrSameJump(i, j, questions)) {
					violations.push({ parentIndex: i, optionIndex: oi });
				}
			}
		} else {
			const j =
				q.jump_to_question != null && q.jump_to_question !== ""
					? q.jump_to_question
					: q.jumpTo;
			if (isBackwardOrSameJump(i, j, questions)) {
				violations.push({ parentIndex: i, optionIndex: null });
			}
		}
	}
	return violations;
}

/**
 * Reset any backward/same-index jumps to `getDefaultForwardJumpTarget` for that parent row.
 */
export function fixBackwardJumpsInSurvey(questions) {
	if (!Array.isArray(questions)) {
		return questions;
	}
	const next = questions.map((q) => ({
		...q,
		jumpTo: Array.isArray(q.jumpTo) ? [...q.jumpTo] : q.jumpTo,
	}));
	for (let i = 0; i < next.length; i++) {
		const q = next[i];
		if (!isQuestionLogic(q)) {
			continue;
		}
		const def = getDefaultForwardJumpTarget(i + 1, next);
		if (q.questionType === "singlechoice") {
			const oc = Math.max(
				extractOptionLabelsFromQuestion(q).length,
				Array.isArray(q.jumpTo) ? q.jumpTo.length : 0,
			);
			let jumpTo = Array.isArray(q.jumpTo) ? [...q.jumpTo] : [];
			while (jumpTo.length < oc) {
				jumpTo.push(def);
			}
			let changed = false;
			for (let oi = 0; oi < oc; oi++) {
				const combined = { ...q, jumpTo };
				const j = getSingleChoiceJumpTarget(combined, oi);
				if (isBackwardOrSameJump(i, j, next)) {
					jumpTo[oi] = def;
					changed = true;
				}
			}
			if (changed) {
				let updated = { ...q, jumpTo };
				updated = syncSingleChoiceOptionJumpFields(updated);
				next[i] = updated;
			}
		} else {
			const j =
				q.jump_to_question != null && q.jump_to_question !== ""
					? q.jump_to_question
					: q.jumpTo;
			if (isBackwardOrSameJump(i, j, next)) {
				next[i] = { ...q, jump_to_question: def, jumpTo: def };
			}
		}
	}
	return next;
}

export function resolveTargetIndex(rawValue, questions) {
	if (
		rawValue === "End Survey" ||
		rawValue === null ||
		rawValue === undefined ||
		rawValue === ""
	) {
		return null;
	}
	if (typeof rawValue === "object" && rawValue !== null && "value" in rawValue) {
		return resolveTargetIndex(rawValue.value, questions);
	}
	const num = Number(rawValue);
	if (!Number.isFinite(num)) {
		return -1;
	}
	const idToIndex = buildIdToIndex(questions);
	if (idToIndex.has(num)) {
		return idToIndex.get(num);
	}
	const posIdx = num >= 1 && num <= questions.length ? num - 1 : -1;
	return posIdx >= 0 ? posIdx : -1;
}

/** Stable jump target id: DB row id when present, else sequential list `id`. */
export function getSurveyJumpTargetId(targetQuestion) {
	if (!targetQuestion || typeof targetQuestion !== "object") {
		return "End Survey";
	}
	if (targetQuestion._surveyQuestionPk != null && targetQuestion._surveyQuestionPk !== "") {
		return Number(targetQuestion._surveyQuestionPk);
	}
	if (targetQuestion.id != null && targetQuestion.id !== "") {
		return Number(targetQuestion.id);
	}
	return "End Survey";
}

/**
 * Default jump when enabling logic: the next question in list order, or End Survey.
 * `questionno` is 1-based (matches QuestionInput / survey builder UI).
 */
export function getDefaultForwardJumpTarget(questionno, questionlist) {
	if (!Array.isArray(questionlist) || questionlist.length === 0) {
		return "End Survey";
	}
	const n = Number(questionno);
	if (!Number.isFinite(n) || n < 1 || n >= questionlist.length) {
		return "End Survey";
	}
	return getSurveyJumpTargetId(questionlist[n]);
}

/** Convert a stored jump (legacy 1-based position or id) to canonical question id / End Survey. */
export function normalizeJumpValueToQuestionId(rawValue, questions) {
	if (
		rawValue === "End Survey" ||
		rawValue === null ||
		rawValue === undefined ||
		rawValue === ""
	) {
		return "End Survey";
	}
	if (typeof rawValue === "object" && rawValue !== null && "value" in rawValue) {
		return normalizeJumpValueToQuestionId(rawValue.value, questions);
	}
	const idx = resolveTargetIndex(rawValue, questions);
	if (idx === null || idx === -1) {
		return "End Survey";
	}
	return getSurveyJumpTargetId(questions[idx]);
}

/** After load, remap jump fields from sort positions to question ids. */
export function remapSurveyQuestionJumpTargetsToIds(questions) {
	if (!Array.isArray(questions) || questions.length === 0) {
		return questions;
	}
	const snap = questions;
	return snap.map((q) => {
		if (!q || typeof q !== "object") {
			return q;
		}
		const next = { ...q };
		if (Array.isArray(q.jumpTo) && q.jumpTo.length > 0) {
			next.jumpTo = q.jumpTo.map((j) => normalizeJumpValueToQuestionId(j, snap));
		}
		if (q.jump_to_question != null && q.jump_to_question !== "") {
			next.jump_to_question = normalizeJumpValueToQuestionId(q.jump_to_question, snap);
		}
		return next;
	});
}

/** 1-based "Question N" label number for a jump target, or null for end/invalid. */
export function resolveJumpTargetDisplayNumber(jumpValue, questions) {
	if (
		jumpValue === "End Survey" ||
		jumpValue === null ||
		jumpValue === undefined ||
		jumpValue === ""
	) {
		return null;
	}
	const idx = resolveTargetIndex(jumpValue, questions);
	if (idx === null || idx === -1) {
		return null;
	}
	return idx + 1;
}

/** `questionno` is 1-based current question index in the list. */
export function isJumpToNextQuestion(jumpValue, questionno, questions) {
	if (
		jumpValue === "End Survey" ||
		jumpValue === null ||
		jumpValue === undefined ||
		jumpValue === ""
	) {
		return false;
	}
	const tIdx = resolveTargetIndex(jumpValue, questions);
	if (tIdx === null || tIdx === -1) {
		return false;
	}
	const n = Number(questionno);
	return Number.isFinite(n) && tIdx === n;
}

/** Label string for survey jump dropdowns (Edit flow uses "Next Question" casing). */
export function formatSurveyJumpDropdownLabel(jumpValue, questionno, questionlist) {
	const ql = Array.isArray(questionlist) ? questionlist : [];
	if (jumpValue === "End Survey" || jumpValue === null || jumpValue === undefined || jumpValue === "") {
		return "End survey";
	}
	if (isJumpToNextQuestion(jumpValue, questionno, ql)) {
		return "Next Question";
	}
	const disp = resolveJumpTargetDisplayNumber(jumpValue, ql);
	return disp != null ? `Question ${disp}` : "End survey";
}

export function getSingleChoiceJumpTarget(q, optionIndex) {
	if (Array.isArray(q.jumpTo) && q.jumpTo[optionIndex] != null && q.jumpTo[optionIndex] !== "") {
		return q.jumpTo[optionIndex];
	}
	if (Array.isArray(q.option) && q.option[optionIndex]) {
		const opt = q.option[optionIndex];
		const j = opt.jump_to_question != null ? opt.jump_to_question : opt.jumpTo;
		if (j != null && j !== "") {
			return j;
		}
	}
	return "End Survey";
}

/** Sentinel index meaning “end survey” in successor / cycle helpers. */
export const SURVEY_JUMP_END_SENTINEL = -1;

/**
 * Successors for participant flow from `questionIndex`: linear next question when logic is off,
 * otherwise all jump targets (singlechoice = union of option jumps).
 */
export function getSurveyJumpSuccessorIndices(questionIndex, questions) {
	const n = Array.isArray(questions) ? questions.length : 0;
	const q = questions[questionIndex];
	if (!q || n === 0) {
		return [SURVEY_JUMP_END_SENTINEL];
	}
	if (!isQuestionLogic(q)) {
		return questionIndex + 1 < n ? [questionIndex + 1] : [SURVEY_JUMP_END_SENTINEL];
	}
	if (q.questionType === "singlechoice") {
		const oc = Math.max(
			extractOptionLabelsFromQuestion(q).length,
			Array.isArray(q.jumpTo) ? q.jumpTo.length : 0,
		);
		const out = new Set();
		for (let oi = 0; oi < oc; oi++) {
			const j = getSingleChoiceJumpTarget(q, oi);
			const tIdx = resolveTargetIndex(j, questions);
			if (tIdx === null || tIdx === -1) {
				out.add(SURVEY_JUMP_END_SENTINEL);
			} else {
				out.add(tIdx);
			}
		}
		return out.size === 0 ? [SURVEY_JUMP_END_SENTINEL] : [...out];
	}
	const j =
		q.jump_to_question != null && q.jump_to_question !== ""
			? q.jump_to_question
			: q.jumpTo;
	const tIdx = resolveTargetIndex(j, questions);
	if (tIdx === null || tIdx === -1) {
		return [SURVEY_JUMP_END_SENTINEL];
	}
	return [tIdx];
}

/**
 * True if some forward path from question 0 revisits a question before ending (infinite loop risk).
 */
export function surveyJumpGraphHasCycleFromStart(questions) {
	if (!Array.isArray(questions) || questions.length === 0) {
		return false;
	}
	const visiting = new Set();

	function dfs(node) {
		if (node === SURVEY_JUMP_END_SENTINEL || node < 0 || node >= questions.length) {
			return false;
		}
		if (visiting.has(node)) {
			return true;
		}
		visiting.add(node);
		const succ = getSurveyJumpSuccessorIndices(node, questions);
		for (let i = 0; i < succ.length; i++) {
			const nx = succ[i];
			if (nx === SURVEY_JUMP_END_SENTINEL || nx < 0) {
				continue;
			}
			if (dfs(nx)) {
				return true;
			}
		}
		visiting.delete(node);
		return false;
	}

	return dfs(0);
}

function assignSequentialQuestionIdsForValidation(questions) {
	if (!Array.isArray(questions)) {
		return [];
	}
	return questions.map((q, index) => ({ ...q, id: index + 1 }));
}

/**
 * Parse legacy `test.surveyQuestion` JSON strings into ordered rows with canonical jump ids
 * (same idea as the survey builder load path).
 */
export function parseSurveyQuestionJsonStringsForJumpValidation(surveyQuestionStrings) {
	if (!Array.isArray(surveyQuestionStrings)) {
		return [];
	}
	const parsed = [];
	for (let i = 0; i < surveyQuestionStrings.length; i++) {
		try {
			const raw = surveyQuestionStrings[i];
			const q =
				typeof raw === "string"
					? JSON.parse(raw)
					: raw && typeof raw === "object"
						? { ...raw }
						: null;
			if (!q || typeof q !== "object") {
				continue;
			}
			const copy = { ...q };
			delete copy.id;
			delete copy.questionid;
			parsed.push(copy);
		} catch {
			continue;
		}
	}
	return remapSurveyQuestionJumpTargetsToIds(assignSequentialQuestionIdsForValidation(parsed));
}

export function validateSurveyQuestionStringsJumpLogic(surveyQuestionStrings) {
	const questions = parseSurveyQuestionJsonStringsForJumpValidation(surveyQuestionStrings);
	if (questions.length === 0) {
		return { ok: true };
	}

	for (let i = 0; i < questions.length; i++) {
		const q = questions[i];
		if (!isQuestionLogic(q)) {
			continue;
		}
		if (q.questionType === "singlechoice") {
			const errs = q.error;
			if (Array.isArray(errs)) {
				for (let oi = 0; oi < errs.length; oi++) {
					if (errs[oi] == 1) {
						return { ok: false, reason: "flag" };
					}
				}
			}
		} else if (q.error == 1 || q.error === true) {
			return { ok: false, reason: "flag" };
		}
	}

	if (findBackwardJumpViolations(questions).length > 0) {
		return { ok: false, reason: "backward" };
	}

	if (surveyJumpGraphHasCycleFromStart(questions)) {
		return { ok: false, reason: "loop" };
	}

	return { ok: true };
}

export function extractOptionLabelsFromQuestion(q) {
	if (!q) return [];
	if (Array.isArray(q.options) && q.options.length > 0) {
		return q.options.map((o) => (typeof o === "string" ? o : o?.label ?? ""));
	}
	if (Array.isArray(q.option) && q.option.length > 0) {
		return q.option.map((o) => (typeof o === "string" ? o : o?.label ?? ""));
	}
	return [];
}

/**
 * Each rule: another question's logic targets `targetIndex` (0-based).
 * `fromDisplay` / `toDisplay` are 1-based question numbers for UI (e.g. Q11 → Q13).
 */
export function collectSurveyJumpRulesTargetingIndex(targetIndex, questions) {
	const rules = [];
	if (!Array.isArray(questions) || questions.length === 0) {
		return rules;
	}
	if (!Number.isInteger(targetIndex) || targetIndex < 0 || targetIndex >= questions.length) {
		return rules;
	}
	const toDisplay = targetIndex + 1;
	for (let i = 0; i < questions.length; i++) {
		if (i === targetIndex) {
			continue;
		}
		const q = questions[i];
		if (!isQuestionLogic(q)) {
			continue;
		}
		const fromDisplay = i + 1;
		if (q.questionType === "singlechoice") {
			const oc = Math.max(
				extractOptionLabelsFromQuestion(q).length,
				Array.isArray(q.jumpTo) ? q.jumpTo.length : 0,
			);
			for (let oi = 0; oi < oc; oi++) {
				const j = getSingleChoiceJumpTarget(q, oi);
				const ti = resolveTargetIndex(j, questions);
				if (ti === targetIndex) {
					rules.push({
						parentIndex: i,
						optionIndex: oi,
						fromDisplay,
						toDisplay,
					});
				}
			}
		} else {
			const j =
				q.jump_to_question != null && q.jump_to_question !== ""
					? q.jump_to_question
					: q.jumpTo;
			const ti = resolveTargetIndex(j, questions);
			if (ti === targetIndex) {
				rules.push({
					parentIndex: i,
					optionIndex: null,
					fromDisplay,
					toDisplay,
				});
			}
		}
	}
	return rules;
}

/**
 * Remove question `delIdx` and reset any logic jumps that pointed at it to the default
 * next question in order or End Survey (`getDefaultForwardJumpTarget`).
 */
export function applySurveyQuestionDeleteWithJumpResets(questions, delIdx) {
	if (!Array.isArray(questions) || delIdx < 0 || delIdx >= questions.length) {
		return [...questions];
	}
	const rules = collectSurveyJumpRulesTargetingIndex(delIdx, questions);
	const next = questions.filter((_, i) => i !== delIdx).map((q) => ({
		...q,
		jumpTo: Array.isArray(q.jumpTo) ? [...q.jumpTo] : q.jumpTo,
	}));

	const oldToNew = (oldIdx) => (oldIdx < delIdx ? oldIdx : oldIdx - 1);

	const byParent = new Map();
	for (const r of rules) {
		if (!byParent.has(r.parentIndex)) {
			byParent.set(r.parentIndex, []);
		}
		byParent.get(r.parentIndex).push(r);
	}

	for (const [, rlist] of byParent) {
		const oldP = rlist[0].parentIndex;
		const np = oldToNew(oldP);
		if (np < 0 || np >= next.length) {
			continue;
		}
		let q = next[np];
		const defaultJump = getDefaultForwardJumpTarget(np + 1, next);

		if (q.questionType === "singlechoice") {
			const oc = Math.max(
				extractOptionLabelsFromQuestion(q).length,
				Array.isArray(q.jumpTo) ? q.jumpTo.length : 0,
			);
			let jumpTo = Array.isArray(q.jumpTo) ? [...q.jumpTo] : [];
			while (jumpTo.length < oc) {
				jumpTo.push(getDefaultForwardJumpTarget(np + 1, next));
			}
			for (const r of rlist) {
				if (
					r.optionIndex !== null &&
					r.optionIndex >= 0 &&
					r.optionIndex < jumpTo.length
				) {
					jumpTo[r.optionIndex] = defaultJump;
				}
			}
			q = { ...q, jumpTo };
		} else {
			q = { ...q, jump_to_question: defaultJump };
		}
		next[np] = q;
	}

	return next;
}

export function anySurveyQuestionJumpsToIndex(targetIndex, questions) {
	return collectSurveyJumpRulesTargetingIndex(targetIndex, questions).length > 0;
}

function putPayloadNullableImageId(v) {
	if (v == null || v === "") return null;
	return v;
}

function putPayloadNullableImageUrl(v) {
	if (v == null || v === "") return null;
	return String(v).trim().replace(/^\/+/, "");
}

function mapJumpToApi(value, questions) {
	if (value === "End Survey" || value === null || value === undefined || value === "") {
		return "End Survey";
	}
	if (typeof value === "object" && value !== null && "value" in value) {
		return mapJumpToApi(value.value, questions);
	}
	const idx = resolveTargetIndex(value, questions);
	if (idx === null) return "End Survey";
	if (idx === -1) return "End Survey";
	return idx + 1;
}

function remapJumpTargetToOrder(rawValue, questions) {
	if (
		rawValue === "End Survey" ||
		rawValue === null ||
		rawValue === undefined ||
		rawValue === ""
	) {
		return "End Survey";
	}
	if (typeof rawValue === "object" && rawValue !== null && "value" in rawValue) {
		return remapJumpTargetToOrder(rawValue.value, questions);
	}
	const idx = resolveTargetIndex(rawValue, questions);
	if (idx === null || idx === -1) {
		return "End Survey";
	}
	return idx + 1;
}

/**
 * Remap jump targets to 1-based positions; set questionid = index + 1.
 */
export function renumberSurveyQuestionsByOrder(questions) {
	if (!Array.isArray(questions)) {
		return [];
	}

	return questions.map((q, index) => {
		if (!q) return q;
		const position = index + 1;
		const next = { ...q, questionid: position };

		if (!isQuestionLogic(q)) {
			return next;
		}

		if (q.questionType === "singlechoice") {
			const optionCount = Math.max(
				Array.isArray(q.jumpTo) ? q.jumpTo.length : 0,
				Array.isArray(q.options) ? q.options.length : 0,
				Array.isArray(q.option) ? q.option.length : 0
			);
			const jumpTo = [];
			for (let oi = 0; oi < optionCount; oi++) {
				jumpTo.push(remapJumpTargetToOrder(getSingleChoiceJumpTarget(q, oi), questions));
			}
			next.jumpTo = jumpTo;
		} else {
			const jumpVal = q.jump_to_question != null ? q.jump_to_question : q.jumpTo;
			const remapped = remapJumpTargetToOrder(jumpVal, questions);
			next.jump_to_question = remapped;
			next.jumpTo = remapped;
		}

		return next;
	});
}

function mapQuestionOptionsToPut(q, questions) {
	const labels = extractOptionLabelsFromQuestion(q);
	const optionImages = Array.isArray(q.optionImages) ? q.optionImages : [];
	const withPerOptionJump = q.questionType === "singlechoice";
	const logic = isQuestionLogic(q);

	return labels.map((label, oi) => {
		const img = optionImages[oi] || {};
		const entry = {
			label: label ?? "",
			image_id: img.image_id != null && img.image_id !== "" ? img.image_id : "",
			image_url:
				img.image_url != null && img.image_url !== ""
					? String(img.image_url).trim().replace(/^\/+/, "")
					: "",
		};
		if (withPerOptionJump) {
			const jumpVal = getSingleChoiceJumpTarget(q, oi);
			entry.jump_to_question = logic ? mapJumpToApi(jumpVal, questions) : null;
		}
		return entry;
	});
}

function applyPutExtraFieldsToItem(item, q) {
	if (!item || !q) return item;

	if (q.questionType === "multiplechoice") {
		if (q.min != null && q.min !== "") item.min = q.min;
		if (q.max != null && q.max !== "") item.max = q.max;
		if (q.exact != null && q.exact !== "") item.exact = q.exact;
	}
	if (q.questionType === "rankingscale" && q.randomize_ranking_options != null) {
		item.randomize_ranking_options =
			q.randomize_ranking_options === 1 || q.randomize_ranking_options === true ? 1 : 0;
	}
	if (q.questionType === "likertscale" || q.questionType === "likertscale_single") {
		if (Array.isArray(q.subQuestions)) item.subQuestions = q.subQuestions;
		if (q.scale_length != null && q.scale_length !== "") item.scale_length = q.scale_length;
		if (q.scale_type != null && q.scale_type !== "") item.scale_type = q.scale_type;
	}
	if (q.questionType === "ratingscale") {
		if (Array.isArray(q.labels)) item.labels = q.labels;
		if (q.scale_length != null && q.scale_length !== "") item.scale_length = q.scale_length;
		if (q.scale_type != null && q.scale_type !== "") item.scale_type = q.scale_type;
	}
	return item;
}

/**
 * @param {Array<object>} questions – builder list (same order as UI)
 */
export function surveyQuestionsToPutPayload(questions) {
	if (!Array.isArray(questions)) {
		return [];
	}

	const ordered = renumberSurveyQuestionsByOrder(questions.map((q) => ({ ...q })));

	return ordered.map((q, index) => {
		const sort_order = index + 1;
		const logic = isQuestionLogic(q);
		const isOptional = q.is_optional === 1 || q.is_optional === true;

		const item = {
			questionType: q.questionType,
			question: q.question ?? "",
			is_optional: isOptional,
			is_logic: logic ? 1 : 0,
			image_id: putPayloadNullableImageId(q.image_id),
			image_url: putPayloadNullableImageUrl(q.image_url),
			sort_order,
		};

		if (q._surveyQuestionPk != null && q._surveyQuestionPk !== "") {
			item.id = Number(q._surveyQuestionPk);
		}

		item.is_ai_generated =
			q.is_ai_generated !== undefined && q.is_ai_generated !== null ? q.is_ai_generated : 0;

		if (q.questionType === "singlechoice") {
			const labels = extractOptionLabelsFromQuestion(q);
			const optionCount = labels.length;
			const jumpTo = [];
			const error = [];
			const errorValue = [];
			const manuallyupdated = [];
			const logicupdated = [];
			const optionImages = [];

			for (let oi = 0; oi < optionCount; oi++) {
				if (logic) {
					const j = getSingleChoiceJumpTarget(q, oi);
					jumpTo.push(mapJumpToApi(j, ordered));
				} else {
					jumpTo.push(null);
				}
				error.push(Array.isArray(q.error) ? Number(q.error[oi] ?? 0) : 0);
				errorValue.push(Array.isArray(q.errorValue) ? (q.errorValue[oi] ?? "") : "");
				manuallyupdated.push(
					Array.isArray(q.manuallyupdated) ? Boolean(q.manuallyupdated[oi]) : false
				);
				logicupdated.push(
					Array.isArray(q.logicupdated) ? Boolean(q.logicupdated[oi]) : false
				);
				const rawImg = Array.isArray(q.optionImages) ? q.optionImages[oi] : undefined;
				const img =
					rawImg != null && typeof rawImg === "object" ? rawImg : {};
				optionImages.push({
					image_id: putPayloadNullableImageId(img.image_id),
					image_url: putPayloadNullableImageUrl(img.image_url),
				});
			}

			item.options = labels;
			item.jumpTo = jumpTo;
			item.jump_to_question = null;
			item.error = error;
			item.errorValue = errorValue;
			item.manuallyupdated = manuallyupdated;
			item.logicupdated = logicupdated;
			item.optionImages = optionImages;
			return item;
		}

		item.jump_to_question = null;
		item.jumpTo = null;
		item.error = 0;
		item.errorvalue = "";
		item.manuallyupdated = false;
		item.logicupdated = false;

		if (logic && q.questionType !== "singlechoice") {
			const jumpVal = q.jump_to_question != null ? q.jump_to_question : q.jumpTo;
			const mapped = mapJumpToApi(jumpVal, ordered);
			item.jump_to_question = mapped;
			item.jumpTo = mapped;
			const err = q.error;
			item.error = err != null && err !== "" ? Number(err) : 0;
			item.errorvalue =
				q.errorvalue != null && q.errorvalue !== ""
					? String(q.errorvalue)
					: q.errorValue != null && q.errorValue !== ""
						? String(q.errorValue)
						: "";
			item.manuallyupdated = Boolean(q.manuallyupdated);
			item.logicupdated = Boolean(q.logicupdated);
		}

		if (TYPES_WITH_PUT_OPTIONS.includes(q.questionType)) {
			item.options = extractOptionLabelsFromQuestion(q);
			item.option = mapQuestionOptionsToPut(q, ordered);
		}

		applyPutExtraFieldsToItem(item, q);
		return item;
	});
}
